/**
 * NCALayer WebSocket client — подписание challenge через ЭЦП НУЦ РК.
 *
 * Использует протокол модуля kz.gov.pki.knca.basics (актуальный для NCALayer 1.4+).
 * Документация: https://pki.gov.kz/ncalayer-2/ и https://www.npmjs.com/package/ncalayer-js-client
 *
 * Зависимостей нет — нативный WebSocket в браузере.
 */

const NCALAYER_WS_URL = "wss://127.0.0.1:13579/";

const STORAGE_OPTIONS = [
  "PKCS12",    // .p12 файл (физлицо + юрлицо)
  "AKKaztokenStore", // токен Kaztoken
  "AKKZIDCardStore", // токен ID-карты
  "AKKZIDCard",
  "KAZTOKEN",
];

function isLikelyDesktop() {
  // NCALayer работает только на десктопе (Win/macOS/Linux)
  return !/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
}

/**
 * Connect to NCALayer and sign a challenge with the user's AUTH key.
 *
 * @param {string} challengeB64 - challenge from backend (base64)
 * @param {object} opts - optional: { timeoutMs, signal }
 * @returns {Promise<string>} - base64-encoded CMS/PKCS#7 payload
 *
 * Throws Error with one of:
 *  - "ECP_NCALAYER_NOT_AVAILABLE" (NCALayer не запущен)
 *  - "ECP_MOBILE_NOT_SUPPORTED"
 *  - "ECP_USER_CANCELLED"
 *  - "ECP_NCALAYER_ERROR" (with details in .ncaCode and .message)
 */
export function signChallengeWithEcp(challengeB64, opts = {}) {
  if (!isLikelyDesktop()) {
    const err = new Error("ECP_MOBILE_NOT_SUPPORTED");
    err.userMessage = "ЭЦП работает только на компьютере. Воспользуйтесь входом по e-mail.";
    return Promise.reject(err);
  }

  const timeoutMs = opts.timeoutMs ?? 60000;

  return new Promise((resolve, reject) => {
    let ws;
    let connected = false;
    let signRequestSent = false;
    let ncaVersion = null;

    const timer = setTimeout(() => {
      try { ws?.close(); } catch (_e) { /* ignore */ }
      const err = new Error(connected ? "ECP_TIMEOUT" : "ECP_NCALAYER_NOT_AVAILABLE");
      err.userMessage = connected
        ? "ЭЦП-приложение не ответило — попробуйте ещё раз."
        : "NCALayer не запущен. Запустите его и попробуйте снова.";
      reject(err);
    }, timeoutMs);

    try {
      ws = new WebSocket(NCALAYER_WS_URL);
    } catch (e) {
      clearTimeout(timer);
      const err = new Error("ECP_NCALAYER_NOT_AVAILABLE");
      err.userMessage = "Не удалось открыть соединение с NCALayer.";
      return reject(err);
    }

    const buildSignRequest = () => ({
      // kz.gov.pki.knca.basics.sign — NCALayer 1.4+ API
      module: "kz.gov.pki.knca.basics",
      method: "sign",
      args: {
        allowedStorages: STORAGE_OPTIONS,
        format: "cms",
        data: challengeB64,
        signingParams: {
          decode: "true",
          encapsulate: "true",
          digested: "false",
          tsaProfile: {},
        },
        // signerParams.extKeyUsageOids — обязательное поле для NCALayer
        // (пустой массив = разрешить любые EKU, включая ключи подписи).
        signerParams: {
          extKeyUsageOids: [],
        },
        locale: "ru",
      },
    });

    const sendSignRequest = () => {
      if (signRequestSent) return;
      signRequestSent = true;
      try {
        const req = buildSignRequest();
        try { console.info("[NCALayer request]", req); } catch (_e) { /* ignore */ }
        ws.send(JSON.stringify(req));
      } catch (e) {
        clearTimeout(timer);
        reject(new Error("ECP_NCALAYER_ERROR: " + e.message));
      }
    };

    ws.onopen = () => {
      connected = true;
      // Не отправляем запрос сразу — сначала дождёмся greeting от NCALayer
      // (NCALayer 1.4+ присылает {result: {version: "1.4"}} при подключении).
      // Но если greeting не придёт за 1 сек — отправим запрос сами (на случай старых версий).
      setTimeout(() => {
        if (!signRequestSent) {
          try { console.info("[NCALayer] greeting timeout, sending sign request anyway"); } catch (_e) { /* ignore */ }
          sendSignRequest();
        }
      }, 1000);
    };

    ws.onmessage = (ev) => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch (_e1) {
        clearTimeout(timer);
        try { ws.close(); } catch (_e2) { /* ignore */ }
        const err = new Error("ECP_NCALAYER_BAD_RESPONSE");
        err.userMessage = "NCALayer прислал нечитаемый ответ.";
        err.rawResponse = String(ev.data).slice(0, 500);
        return reject(err);
      }
      try { console.info("[NCALayer message]", msg); } catch (_e) { /* ignore */ }

      // 1. Greeting (NCALayer 1.4+): {"result": {"version": "1.4"}}
      //    Игнорируем, отправляем sign-запрос.
      if (msg && msg.result && typeof msg.result === "object" && msg.result.version && !signRequestSent) {
        ncaVersion = msg.result.version;
        try { console.info("[NCALayer] greeting received, version:", ncaVersion); } catch (_e) { /* ignore */ }
        sendSignRequest();
        return; // wait for next message — actual sign response
      }

      // 2. Sign response — clear timer, close ws, resolve/reject
      clearTimeout(timer);
      try { ws.close(); } catch (_e3) { /* ignore */ }

      // Successful sign:
      //   { status: true, body: { result: ["-----BEGIN CMS-----\n...-----END CMS-----"] } }  ← NCALayer 1.4+ PEM array
      //   { status: true, body: { result: "<base64-CMS>" } }    ← новый формат
      //   { result: "<base64-CMS>" }                             ← старый формат
      //   { responseObject: "<base64-CMS>" }                     ← legacy
      const extractB64 = (raw) => {
        if (Array.isArray(raw)) raw = raw[0];
        if (typeof raw !== "string") return null;
        // Strip PEM markers if present (NCALayer 1.4+ wraps CMS in BEGIN/END markers)
        const stripped = raw
          .replace(/-----BEGIN [^-]+-----/g, "")
          .replace(/-----END [^-]+-----/g, "")
          .replace(/\s+/g, "");
        return stripped.length > 50 ? stripped : null;
      };
      const ok = msg.status === true && msg.body?.result !== undefined ? extractB64(msg.body.result) : null;
      if (ok) return resolve(ok);
      const direct = extractB64(msg.result);
      if (direct) return resolve(direct);
      const legacy = extractB64(msg.responseObject);
      if (legacy) return resolve(legacy);

      // Error variants
      const codeStr = msg.code || msg.errorCode || msg.body?.errorCode || msg.result?.errorCode || "";
      const msgStr = msg.message || msg.errorMessage || msg.body?.errorMessage || msg.result?.errorMessage || "";
      const looksCancelled = /cancel|отмен/i.test(`${codeStr} ${msgStr}`)
        || codeStr === "USER_CANCELLED" || codeStr === "100" || codeStr === "1.0.273.0";
      if (looksCancelled) {
        const err = new Error("ECP_USER_CANCELLED");
        err.userMessage = "Подписание отменено пользователем.";
        err.ncaCode = codeStr;
        err.ncaMessage = msgStr;
        err.rawResponse = msg;
        return reject(err);
      }
      const err = new Error("ECP_NCALAYER_ERROR");
      const detailParts = [];
      if (codeStr) detailParts.push(`код: ${codeStr}`);
      if (msgStr) detailParts.push(msgStr);
      err.userMessage = detailParts.length
        ? `NCALayer: ${detailParts.join(" — ")}`
        : `NCALayer вернул ответ без описания ошибки. Полный ответ: ${JSON.stringify(msg).slice(0, 300)}`;
      err.ncaCode = codeStr;
      err.ncaMessage = msgStr;
      err.rawResponse = msg;
      err.ncaVersion = ncaVersion;
      reject(err);
    };

    ws.onerror = () => {
      clearTimeout(timer);
      try { ws.close(); } catch (_e) { /* ignore */ }
      if (!connected) {
        const err = new Error("ECP_NCALAYER_NOT_AVAILABLE");
        err.userMessage = "NCALayer не запущен или браузер блокирует подключение к 127.0.0.1:13579.";
        reject(err);
      } else {
        const err = new Error("ECP_NCALAYER_ERROR");
        err.userMessage = "Соединение с NCALayer прервано.";
        reject(err);
      }
    };
  });
}

/**
 * Human-readable error message for any error thrown by signChallengeWithEcp.
 */
export function ecpErrorMessage(err) {
  if (!err) return "Неизвестная ошибка ЭЦП";
  if (err.userMessage) return err.userMessage;
  if (err.message?.startsWith("ECP_")) return err.message;
  return String(err.message || err);
}
